import { ClassNames, css } from '@emotion/core'
import dayjs from 'dayjs'
import React from 'react'
import Modal from 'react-modal'

import { breakpoints } from '../../constants/breakPoints'
import { isDesktop } from '../../helpers/common'
import { dateSerializer, useLocalStorage } from '../../hooks/useLocalStorage'
import { useMemoWithDelay } from '../../hooks/useMemoWithDelay'
import { DemoButton } from '../Button'
import { HubspotForm } from '../HubspotForm'
import Image from '../Image'
import { closeButtonStyle } from './ExitIntentPopup'

const LeftCornerPopup: React.FC<{
  title?: string
  description?: string
  withBookDemoButton?: boolean
}> = ({
  title = 'Join our weekly product webinar',
  description = 'In this walkthrough, learn how airfocus helps you manage strategy, understand user needs, prioritize, and align teams around clear roadmaps.',
  withBookDemoButton = false,
}) => {
  if (!isDesktop()) {
    return null
  }

  const [isSubmitted, setIsSubmitted] = React.useState(false)
  const [user, setUser] = React.useState<Array<{ value: string }>>([])
  const [isClosed, setIsClosed] = React.useState(false)

  const [userId] = useLocalStorage('ajs_user_id', undefined)
  const [seenAt, setSeenAt] = useLocalStorage(
    withBookDemoButton ? 'book_demo_seen_at' : 'webinar_seen_at',
    undefined,
    dateSerializer
  )
  const [webinarRegistered, setWebinarRegistered] = useLocalStorage('webinar_registered', undefined, dateSerializer)
  const [demoBooked] = useLocalStorage('demo_booked', undefined, dateSerializer)

  const shouldBeShown = () =>
    !isClosed &&
    !userId &&
    (!seenAt || dayjs().diff(seenAt, 'day') > 0) &&
    (withBookDemoButton
      ? !demoBooked || dayjs().diff(demoBooked, 'day') > 30
      : !webinarRegistered || dayjs().diff(webinarRegistered, 'day') > 30)

  // don't pass seenAt as dependancy because it will hide popup in 20 sec
  const [showPopup, setShowPopup] = useMemoWithDelay(shouldBeShown, 20000, [userId, isClosed], false)

  React.useEffect(() => {
    if (showPopup) {
      setSeenAt(dayjs())
    }
  }, [showPopup])

  React.useEffect(() => {
    const scrollHandler = () => {
      if (!showPopup && window.innerHeight + window.pageYOffset >= document.body.scrollHeight / 2 && shouldBeShown()) {
        setShowPopup(true)
        document.removeEventListener('scroll', scrollHandler)
      }
    }

    const messageHandler = (event: MessageEvent) => {
      if (event.data.type === 'hsFormCallback') {
        if (event.data.eventName === 'onFormSubmit' && event.data.id === '688edd07-84fc-4e56-9ad4-88778dfe8558') {
          setUser(event.data.data)
          setIsSubmitted(true)
          setWebinarRegistered(dayjs())
        }
      }
    }
    if (shouldBeShown()) {
      document.addEventListener('scroll', scrollHandler)
    }

    window.addEventListener('message', messageHandler)

    return () => {
      document.removeEventListener('scroll', scrollHandler)
      document.removeEventListener('message', messageHandler)
    }
  }, [userId, seenAt, showPopup])

  const hidePopup = () => {
    setShowPopup(false)
    setIsClosed(true)
  }

  return (
    <React.Fragment>
      <ClassNames>
        {({ css }) => {
          return (
            <Modal
              id="LeftCornerPopup"
              isOpen={showPopup}
              className={css`
                ${modalStyle}
              `}
              overlayClassName={css`
                ${overlayStyle}
              `}
              ariaHideApp={false}
              onRequestClose={hidePopup}
              shouldFocusAfterRender={false}
              shouldCloseOnOverlayClick={false}
              shouldCloseOnEsc={false}
              closeTimeoutMS={400}
            >
              <div css={modalContainerStyle}>
                <div css={[closeButtonStyle, cancelStyle]} onClick={hidePopup} />
                {withBookDemoButton ? (
                  <Image src="coins/airfocus-coin.webp" alt="airfocus coin" />
                ) : (
                  <div css={imageContainerStyle}>
                    <Image
                      src="popups/live-training-julia-popup.webp"
                      alt="live training airfocus webinar"
                      className="webinar-image"
                    />
                  </div>
                )}
                <div css={contentContainerStyle}>
                  <div className="strong-body-1 center">{title}</div>
                  <div className="body-1 center">{description}</div>
                </div>

                {withBookDemoButton ? (
                  <div
                    className="body-1 center"
                    css={css`
                      margin-top: 1rem;
                    `}
                  >
                    <DemoButton type="primary" />
                  </div>
                ) : isSubmitted ? (
                  <div className="body-1 center" css={submittedMessageStyle}>
                    <div css={submittedImageStyle}>
                      <Image src="popups/confetti.webp" alt="confetti" />
                    </div>
                    <h4 className="heading4 center">You're in, {user[1].value}!</h4>
                    Details have been sent to your inbox ({user[2].value}). <br />
                    See you on {user[0].value}.
                  </div>
                ) : (
                  <div css={hubspotFormStyle}>
                    <HubspotForm hubspotFormId="688edd07-84fc-4e56-9ad4-88778dfe8558" />
                  </div>
                )}
              </div>
            </Modal>
          )
        }}
      </ClassNames>
    </React.Fragment>
  )
}

export default LeftCornerPopup

const modalContainerStyle = css`
  transition: all 400ms ease-in-out;
  color: rgb(59, 68, 77);

  .heading4 {
    margin: 1rem 0;
  }

  .webinar-image {
    position: absolute;
    left: 0;
    z-index: -1;
    height: 180px;
    width: 100%;
    object-fit: cover;
    object-position: top;
  }

  .hs_submit {
    input {
      width: 280px;
      max-width: 280px;
      height: 80px;
      font-size: 26px;
      font-weight: 600;
    }
  }

  label,
  select,
  input {
    font-size: 0.875rem;
  }

  .actions {
    width: 60%;
  }

  .hs-form {
    margin-bottom: 0 !important;
  }
`

const overlayStyle = ``

const modalStyle = `
  overflow-y: auto;
  z-index: 2147483004;
  pointer-events: auto;
  position: fixed;
  background-color: rgb(255, 255, 255);
  color: rgb(51, 51, 51);
  max-width: 400px;
  box-shadow: rgba(0, 0, 0, 0.2) 0px 0px 50px 0px;
  margin: 0px auto;
  padding: 2rem 1.5rem 1rem 1.5rem;
  border-radius: 0.5rem;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  bottom: 1rem;
  left: 1rem;

  @media screen and (max-width: ${breakpoints.small}px) {
    padding: 1.5rem 1rem 0.5rem;
    left: 50%;
    transform: translateX(-50%);
    bottom: 0.5rem;
    width: 95%;
  }

  @media screen and (max-height: 750px) {
    transform: scale(0.8);
    bottom: -3rem;
    left: -1rem;
  }
`

const hubspotFormStyle = css`
  form {
    display: grid;
    grid-gap: 0;
    margin: 1rem auto 4rem;
    .actions {
      width: 100%;
      margin-top: 1rem;
      input {
        max-width: 100%;
        width: 100%;
        height: 3rem;
      }
      .hs-input {
        margin-top: 0.3rem;
      }
    }

    .form-columns-1:last-of-type {
      & .legal-consent-container > div > p {
        font-size: 0.75rem;
      }
    }

    fieldset.form-columns-1 .hs-input {
      width: 100% !important;
    }

    @media screen and (max-width: ${breakpoints.small}px) {
      grid-gap: 0.5rem;

      .hs-input {
        margin-top: 0.15rem;
      }

      .hs-webinar_dates_demo_ > .input {
        margin-bottom: 0.55rem;
      }

      .actions {
        margin-top: 0.4rem;
      }
    }
  }
`

const cancelStyle = css`
  width: 26px;
  height: 26px;
  top: 3px;
  right: 3px;

  @media screen and (max-width: ${breakpoints.small}px) {
    transform: scale(0.9);
    top: 0px;
    right: 0px;
  }
`

const contentContainerStyle = css`
  text-align: center;
  margin: 1rem 0;

  .body-1 {
    margin: 0;
  }
`

const imageContainerStyle = css`
  position: relative;
  top: 0;
  left: 0;
  text-align: center;
  height: 180px;
  border-radius: 0.25rem;
  overflow: hidden;

  @media screen and (max-width: ${breakpoints.small}px) {
    height: 170px;
  }
`

const submittedImageStyle = css`
  width: 4.5rem;
  margin: 0 auto 1rem;
`

const submittedMessageStyle = css`
  padding: 2.4rem 0 0.5rem;

  @media screen and (max-width: ${breakpoints.small}px) {
    padding: 3.9rem 0 2rem;
  }
`
